/**
 * 通常フォーマット（標準取込）経路の確認画面用の固定マッピング導出（#4728）。
 *
 * 標準フォーマットはヘッダー名で列が確定するため、ヘッダー行を
 * SHIPMENT_ORDER_CSV_STANDARD_HEADER_FIELDS と突合して ColumnMapping を導出し、
 * 汎用経路と同じ buildPreview（形式チェック・エラー表示・インライン修正）を共用する。
 *
 * 「標準フォーマットらしさ」の判定と「必須項目の充足」は別の懸念として分離する
 * （列が無い必須項目を確認画面の手入力で救う機能は、通常フォーマットにも汎用と同じく効かせる）。
 * - 標準フォーマットらしさ: ヘッダー一致数が STANDARD_FORMAT_MIN_MATCH_THRESHOLD 件以上か
 *   （必須項目に限らず全体の一致数で判定。無関係なファイルが「備考」等の汎用的な列名
 *   1〜数件の偶然一致だけで標準フォーマットとして通ってしまうのを防ぐガード）。
 * - 必須項目の充足: ここでは問わない。ヘッダーが一致しなかった必須項目は
 *   deriveStandardMissingRequiredFields で検出し、呼び出し側が skippedFields 相当として
 *   扱う（確認画面のインライン修正・一括入力で埋められる）。
 *
 * 取り込み・事前チェックの正はサーバー側の標準パーサー（動的列群・カスタムキーマスタ突合を含む）で、
 * この FE 導出は確認画面表示用の近似。
 */

import { SHIPMENT_ORDER_CSV_STANDARD_HEADER_FIELDS } from '@/constants/common/message.constants'

import { findMissingRequiredFields, type ColumnMapping } from './column-mapping'
import { type EffectiveTable } from './csv-import-state'
import { type LogiFieldValue } from './logi-fields'

const HEADER_FIELDS: Record<string, LogiFieldValue> =
  SHIPMENT_ORDER_CSV_STANDARD_HEADER_FIELDS

/**
 * 「標準フォーマットらしい」と判定する最低ヘッダー一致数。
 * 必須項目に限らない全体一致数で見る——1〜2件の偶然一致（「備考」等の汎用的な列名）を
 * 弾きつつ、必須項目の一部が欠けているだけの正当な標準フォーマットは通す閾値として
 * 5件とする（SHIPMENT_ORDER_CSV_STANDARD_HEADER_FIELDS 全25列中の目安）。
 */
export const STANDARD_FORMAT_MIN_MATCH_THRESHOLD = 5

/**
 * 標準ヘッダー名一致で ColumnMapping を導出する（1項目1列の制約を保つ）。
 * ヘッダーが一致しない列は含まれない（呼び出し側が必須項目の欠けを別途判定する）。
 */
export const deriveStandardMapping = (table: EffectiveTable): ColumnMapping => {
  const mapping: ColumnMapping = {}
  const used = new Set<LogiFieldValue>()
  table.headers.forEach((header, columnIndex) => {
    const field = HEADER_FIELDS[header.trim()]
    if (field && !used.has(field)) {
      mapping[columnIndex] = field
      used.add(field)
    }
  })
  return mapping
}

/**
 * ヘッダー行が「標準フォーマットらしい」か（全体一致数が閾値以上か）。
 * 必須項目の充足は問わない——欠けは deriveStandardMissingRequiredFields で扱う。
 */
export const isPlausiblyStandardFormat = (table: EffectiveTable): boolean => {
  const matchCount = table.headers.filter(
    (h) => HEADER_FIELDS[h.trim()] !== undefined
  ).length
  return matchCount >= STANDARD_FORMAT_MIN_MATCH_THRESHOLD
}

/**
 * 導出したマッピングで、ヘッダーが見つからず列に紐づかなかった必須項目の集合。
 * 汎用経路の skippedFields と同じ扱い（確認画面で全行空欄エラー表示→
 * インライン修正/一括入力で埋められる）で preview に渡す。
 */
export const deriveStandardMissingRequiredFields = (
  mapping: ColumnMapping
): Set<LogiFieldValue> => {
  const used = new Set<LogiFieldValue>()
  for (const value of Object.values(mapping)) {
    if (value !== null) used.add(value)
  }
  return new Set(findMissingRequiredFields(used).map((f) => f.value))
}
